/**
 * MobilityTabbedPanel - Panel izquierdo con pestañas para módulo de movilidad
 *
 * @version 1.0
 * @module core/modules/mobility
 *
 * @description
 * Pestañas: Paradas, Rutas, Ciclovías, Peatonal, Estacionamientos, Señalización
 */

import { useState } from 'react';
import {
  MapPin, Route, Bike, Footprints, ParkingSquare, SignpostBig,
} from 'lucide-react';
import { BaseInventoryTab } from '../../layouts/panels/LeftPanel/BaseInventoryTab';
import { LeftPanelToggle } from '../../layouts/panels/LeftPanel/LeftPanel';
import { useRightPanel } from '../../layouts/panels/RightPanel/RightPanelContext';
import { useMapTools } from '../../map-tools';
import { useMobilityElementColorsStore } from './store/element-colors-store';
import { MAPPABLE_ELEMENT_IDS } from './constants/element-colors';
import type { MobilityElementId } from './constants/element-colors';
import '../../layouts/panels/LeftPanel/tabbed-left-panel.css';

type MobilityTabType =
  | 'stops'
  | 'routes'
  | 'bikeways'
  | 'pedestrian'
  | 'parking'
  | 'signage';

interface TabInfo {
  id: MobilityTabType;
  icon: React.ReactNode;
  tooltip: string;
  title: string;
  emptyMessage: string;
}

const TABS: TabInfo[] = [
  { id: 'stops',      icon: <MapPin size={16} />,       tooltip: 'Paradas',          title: 'Paradas',          emptyMessage: 'Sin paradas registradas' },
  { id: 'routes',     icon: <Route size={16} />,        tooltip: 'Rutas',            title: 'Rutas',            emptyMessage: 'Sin rutas registradas' },
  { id: 'bikeways',   icon: <Bike size={16} />,         tooltip: 'Ciclovías',        title: 'Ciclovías',        emptyMessage: 'Sin ciclovías registradas' },
  { id: 'pedestrian', icon: <Footprints size={16} />,   tooltip: 'Zonas peatonales', title: 'Zonas peatonales', emptyMessage: 'Sin zonas peatonales' },
  { id: 'parking',    icon: <ParkingSquare size={16} />, tooltip: 'Estacionamientos', title: 'Estacionamientos', emptyMessage: 'Sin estacionamientos' },
  { id: 'signage',    icon: <SignpostBig size={16} />,  tooltip: 'Señalización',     title: 'Señalización',     emptyMessage: 'Sin señalización registrada' },
];

export function MobilityTabbedPanel(): JSX.Element {
  const [activeTab, setActiveTab] = useState<MobilityTabType>('stops');
  const { canChangeContent } = useRightPanel();
  const { selectElement, getSelectedElement } = useMapTools();
  const getColor = useMobilityElementColorsStore((s) => s.getColor);
  const setColor = useMobilityElementColorsStore((s) => s.setColor);

  const activeTabInfo = TABS.find(t => t.id === activeTab)!;
  const isMappable = MAPPABLE_ELEMENT_IDS.has(activeTab);
  const selectionKey = `mobility-${activeTab}`;
  const activeElementId = getSelectedElement(selectionKey);

  /**
   * Maneja el clic en el código de un elemento
   *
   * @remarks
   * Antes de cambiar de elemento, verifica si hay cambios sin guardar en el panel derecho.
   * Si hay cambios, pregunta al usuario si desea descartarlos.
   * Solo cambia de elemento si el usuario confirma o no hay cambios.
   *
   * Despacha un evento personalizado que puede ser manejado por:
   * - Un listener en el módulo de movilidad
   * - Un panel de detalles (cuando se implemente)
   * - Integración con MapLayout para selección
   */
  const handleCodeClick = async (elementId: string): Promise<void> => {
    console.log('[MobilityTabbedPanel] ====================================');
    console.log(`[MobilityTabbedPanel] handleCodeClick - Elemento clickeado: ${activeTab} - ${elementId}`);

    // Verificar si hay cambios sin guardar ANTES de cambiar
    console.log('[MobilityTabbedPanel] Verificando cambios sin guardar...');
    const canChange = await canChangeContent();

    if (!canChange) {
      console.warn('[MobilityTabbedPanel] ✗ Usuario canceló el cambio (hay cambios sin guardar)');
      console.log('[MobilityTabbedPanel] ====================================');
      return; // Usuario canceló, no cambiar
    }

    console.log('[MobilityTabbedPanel] ✓ Cambios verificados, procediendo con el cambio...');

    // Marcar elemento como seleccionado para resaltado en sidebar
    selectElement(selectionKey, elementId);

    console.log(`[MobilityTabbedPanel] Despachando evento: mobility-${activeTab}-select`);

    // Despachar evento para que otros componentes puedan reaccionar
    window.dispatchEvent(new CustomEvent(`mobility-${activeTab}-select`, {
      detail: { elementId, elementType: activeTab },
    }));

    console.log('[MobilityTabbedPanel] ====================================');
  };

  return (
    <div className="tabbed-left-panel">
      <div className="tabs-header">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            title={tab.tooltip}
            aria-label={tab.tooltip}
          >
            {tab.icon}
          </button>
        ))}
      </div>

      <div className="content-card-wrapper">
        <div className="content-card">
          <div className="tab-content">
            <BaseInventoryTab
              key={activeTab}
              title={activeTabInfo.title}
              emptyMessage={activeTabInfo.emptyMessage}
              eventPrefix={`mobility-${activeTab}`}
              moduleId="mobility"
              elementColor={isMappable ? getColor(activeTab as MobilityElementId) : undefined}
              onElementColorChange={isMappable ? (hex) => setColor(activeTab as MobilityElementId, hex) : undefined}
              onCodeClick={handleCodeClick}
              activeElementId={activeElementId}
            />
          </div>
          <LeftPanelToggle />
        </div>
      </div>
    </div>
  );
}
